import { useState, useEffect, useCallback } from 'react';

const defaultSerializer = JSON.stringify;
const defaultDeserializer = JSON.parse;

export function useLocalStorage(key, initialValue, options = {}) {
  const {
    serializer = defaultSerializer,
    deserializer = defaultDeserializer,
  } = options;

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? deserializer(item) : initialValue;
    } catch (error) {
      // console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = useCallback(
    (value) => {
      try {
        // Allow value to be a function so we have same API as useState
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        
        // Save state
        setStoredValue(valueToStore);
        
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(key, serializer(valueToStore));
        }
      } catch (error) {
        // console.warn(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key, serializer, storedValue]
  );

  // Listen to changes in other tabs/windows
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    function handleStorageChange(event) {
      if (event.key === key && event.newValue !== serializer(storedValue)) {
        try {
          const newValue = event.newValue 
            ? deserializer(event.newValue) 
            : initialValue;
          setStoredValue(newValue);
        } catch (error) {
          // console.warn(`Error parsing localStorage change for key "${key}":`, error);
        }
      }
    }

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, storedValue, initialValue, serializer, deserializer]);

  return [storedValue, setValue];
}

// Optional: Export these utility functions if you need them
export const dateSerializer = (date) => date.toISOString();
export const dateDeserializer = (dateString) => new Date(dateString);